<script setup lang="ts">
import {
  EvaluationParameterDto,
  instanceOfNumericEvaluationParameterDto,
  NumericEvaluationParameterDto,
  NumericEvaluationResultDto,
} from '@/api/generator';
import { toDisplayValue } from '@/utils/numberUtils';
import { I18nKey } from '@/utils/typings';
import JobExecutionTrendViewDialog from '@/views/jobOccurrences/components/JobExecutionTrendViewDialog.vue';
import { getSetupParameterName } from '@/views/tasks/utils.ts';
import { useTranslation } from 'i18next-vue';
import { computed } from 'vue';
import LinearGauge from '../charts/LinearGauge.vue';
import { getLimitedValue } from '../charts/utils/limitedValue';
import CommentInlineDialog from '../comments/CommentInlineDialog.vue';
import EvaluationState from '../evaluation/EvaluationState.vue';

const { t } = useTranslation();

type AnalysisResultOptions = {
  enableComments?: boolean;
  enableTrends?: boolean;
};

const props = withDefaults(
  defineProps<{
    value?: NumericEvaluationResultDto[];
    evaluations?: EvaluationParameterDto[];
    options?: AnalysisResultOptions;
    disabled?: boolean;
  }>(),
  {
    value: undefined,
    evaluations: undefined,
    disabled: false,
    options: () => ({
      enableComments: true,
      enableTrends: true,
    }),
  },
);

const emits = defineEmits<{
  change: [];
}>();

function getResult(evaluation: NumericEvaluationParameterDto) {
  return props.value?.find((r) => r.evaluationParameterId === evaluation.id);
}

const items = computed(() => {
  return props.evaluations?.filter(instanceOfNumericEvaluationParameterDto).map((evaluation) => {
    const result = getResult(evaluation);

    return {
      title: getSetupParameterName(evaluation),
      limitedValue: result ? getLimitedValue(result, evaluation) : undefined,

      deviation: {
        value: result?.deviationValue,
        unitAbbreviation: result?.deviationUnit,
        decimalPlaces: result?.decimalPlaces,
        valueType: result ? 'FloatingPoint' : undefined,
      },

      evaluationInfo: evaluation,

      outcome: result?.evaluationOutcome,

      trend: result?.id,

      result,
    };
  });
});

const headers = computed(() => [
  {
    title: t('Quantity_one'),
    key: 'title',
  },
  {
    title: t('EvaluationType_one'),
    key: 'evaluationInfo',
  },
  {
    title: t('Value'),
    key: 'deviation',
  },
  {
    title: t('Limit_other'),
    key: 'limitedValue',
  },
  {
    title: t('Result_one'),
    key: 'outcome',
  },
  ...(props.options.enableTrends ? [{ key: 'trend', sortable: false }] : []),
  ...(props.options.enableComments ? [{ key: 'comments', sortable: false }] : []),
]);
</script>

<template>
  <v-data-table v-if="items" :headers="headers" :items="items" itemsPerPage="-1" hideDefaultFooter>
    <template #item.title="{ value: title }">
      <v-label class="text-justify">{{ title }}</v-label>
    </template>

    <template #item.evaluationInfo="{ value: { evaluationType } }">
      <div v-if="evaluationType" class="text-justify">
        {{ $t(`EvaluationTypeEnum.${evaluationType}` as I18nKey) }}
      </div>
    </template>

    <template #item.limitedValue="{ item, value: limitedValue }">
      <LinearGauge
        v-if="limitedValue"
        :value="limitedValue"
        :unit="item.deviation.unitAbbreviation ?? undefined"
        :disabled="disabled"
      />
    </template>

    <template #item.deviation="{ value: deviation }">
      {{
        toDisplayValue(deviation.value, {
          fractionDigits: deviation.decimalPlaces,
          valueType: deviation.valueType,
          unitAbbreviation: deviation.unitAbbreviation,
        })
      }}
    </template>

    <template #item.outcome="{ value: outcome }">
      <EvaluationState :value="outcome" :disabled="disabled" class="ml-2" />
    </template>

    <template #item.trend="{ value: evaluationResultId }">
      <JobExecutionTrendViewDialog :evaluationResultId="evaluationResultId" :disabled="disabled" />
    </template>

    <template #item.comments="{ item: { result } }">
      <CommentInlineDialog
        v-if="result"
        :entity="result"
        entityWithCommentType="EvaluationResultOrDataPoint"
        @change="emits('change')"
      />
    </template>
  </v-data-table>
</template>

<style scoped lang="scss">
:deep(.v-data-table__td) {
  vertical-align: top;
  padding-left: 0 !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
  min-height: unset !important;
  height: unset !important;
}
</style>
